<script setup>
// Playground do PowerAgents — AGENTS-PLAYGROUND-01: testa o RASCUNHO, sem caixa de entrada
// conectada e sem publicar (pedido do Andrey, 24/09: agente recém-criado não respondia).
//
// Cada envio vai ao runtime, que roda o turno em memória contra o que está salvo nesta tela. As
// ferramentas rodam de verdade, inclusive as que alteram dados (decisão do Murilo, 25/09: "o
// playground é pra testar o agente normal sem restrição nenhuma"); só o pedido de humano é
// simulado, porque o teste não tem conversa. Nada cria contato, conversa ou card no Kanban, nem
// entra no teto do agente em produção.
//
// A conversa é a da página do link (L-26), em `shared/components/conversaDeTeste`: eram duas telas,
// e cada uma tinha o que a outra não tinha. O que muda é o transporte (`transporteDoRascunho`), que
// fala com o rascunho, e o que vem embaixo de cada resposta: o rastro, recolhível, pelo mesmo filtro
// do rastro publicado (modelo, consumo e JSON cru das ferramentas só para super admin).
//
// A nota de voz é transcrita no servidor com o provedor e a chave do rascunho, e a foto e o PDF (L-23)
// seguem as regras da página do link. O rascunho decide se o microfone aparece, se o arquivo precisa
// de texto junto e se o agente lê o arquivo escolhido: quando não lê, a conversa avisa antes do envio
// (L-31), e aqui o aviso leva a quem pode ligar a leitura para "Voz e anexos".
//
// O modo do Playground (L-27) é do agente (`playground_mode`, escolhido no bloco do link de teste). No
// motor, tudo o que está acima. No canal, a conversa é de verdade, na caixa de teste do agente, como a
// da página do link no mesmo modo (`useConversaDoCanal`, com o `transporteDoCanal`): quem responde é a
// versão publicada, pelo caminho de produção, e é dela que saem o microfone, o texto junto do arquivo
// e a leitura de anexos. A conversa fica na caixa, e volta ao abrir a tela.
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import powerAgentsApi from 'dashboard/api/powerAgents';
import teamsApi from 'dashboard/api/teams';
import CabecalhoDaConversa from 'shared/components/conversaDeTeste/CabecalhoDaConversa.vue';
import ConversaDeTeste from 'shared/components/conversaDeTeste/ConversaDeTeste.vue';
import { useConversaDoMotor } from 'shared/components/conversaDeTeste/useConversaDoMotor';
import { useConversaDoCanal } from 'shared/components/conversaDeTeste/useConversaDoCanal';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SectionShell from './SectionShell.vue';
import RastroDoTurno from './RastroDoTurno.vue';
import { transporteDoRascunho } from '../transporteDoRascunho';
import { transporteDoCanal } from '../transporteDoCanal';
import { transcreveAudio } from '../helpers/transcricao';

const props = defineProps({
  // A página passa `agent` a toda seção. O playground lê do contexto injetado o que precisa
  // para falar com o servidor, e do `agent` o modo do Playground e o que o rascunho diz da voz e
  // dos anexos: se a transcrição está ligada e se ele espera texto junto do arquivo.
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');
// Buscado aqui, como nas seções vizinhas: a lista da store só se enche quando a barra lateral
// principal monta, e a página do agente não a tem. Sem isso o aviso dizia "time #5".
const teams = ref([]);
onMounted(async () => {
  try {
    const { data } = await teamsApi.get();
    teams.value = data || [];
  } catch (e) {
    teams.value = [];
  }
});

function nomeDoTime(id) {
  return teams.value.find(team => team.id === id)?.name || `#${id}`;
}

// A transcrição pode ser desligada DEPOIS que esta tela abriu (em outra aba, por exemplo), e a
// cópia do agente que ela tem segue dizendo "ligada". Quem avisa é o upload, recusado com
// `transcricao_desligada`: o microfone some ali e só volta com uma cópia nova do agente, que vem
// do servidor e diz se a transcrição voltou. Se a busca falhar, a recusa segue escondendo o
// microfone.
const transcricaoRecusada = ref(false);
watch(
  () => props.agent,
  () => {
    transcricaoRecusada.value = false;
  }
);
async function transcricaoDesligada() {
  transcricaoRecusada.value = true;
  try {
    ctx.onSectionSaved(
      await powerAgentsApi.show(ctx.accountId(), ctx.agentId())
    );
  } catch (e) {
    // ver o comentário acima
  }
}

// As duas conversas existem desde o começo, como na página do link, e a do modo é a que a tela usa.
// `reactive` desembrulha os refs de cada uma: a tela lê `conversa.turnos`.
const canal = transporteDoCanal(ctx);
const conversas = {
  motor: reactive(
    useConversaDoMotor(
      transporteDoRascunho(ctx, { aoRecusarTranscricao: transcricaoDesligada })
    )
  ),
  canal: reactive(useConversaDoCanal(canal)),
};
const noCanal = computed(() => props.agent.playground_mode === 'canal');
const conversa = computed(() => conversas[noCanal.value ? 'canal' : 'motor']);
onUnmounted(() =>
  Object.values(conversas).forEach(cadaUma => cadaUma.encerrar())
);

// O agente que saiu do modo canal com a tela aberta (o servidor diz `modo_trocado`): uma cópia nova
// dele traz o modo, e a tela segue nele (o `nextTick` espera a cópia chegar a esta seção). Se a busca
// falhar, a tela fica no modo que tinha.
async function buscarOAgente() {
  try {
    ctx.onSectionSaved(
      await powerAgentsApi.show(ctx.accountId(), ctx.agentId())
    );
    await nextTick();
  } catch (e) {
    // ver o comentário acima
  }
}

// No canal a tela abre como a página do link: a caixa, o que a versão publicada decide e a conversa
// viva de quem testa, que o servidor guarda. Até isso chegar, a conversa espera; se não chega, a tela
// diz, com o tentar de novo.
const estadoDoCanal = ref(null);
const canalNaoAbriu = ref(false);
async function abrirCanal() {
  estadoDoCanal.value = null;
  canalNaoAbriu.value = false;
  try {
    const estado = await canal.estado();
    estadoDoCanal.value = estado;
    conversas.canal.carregar(estado.conversa || []);
  } catch (e) {
    if (canal.saida(e)) await buscarOAgente();
    if (noCanal.value) canalNaoAbriu.value = true;
  }
}
watch(noCanal, ligado => ligado && abrirCanal(), { immediate: true });

// A recusa do modo trocado no meio da conversa: o envio que a recebeu ficou parado. Com o agente de
// novo, a tela segue no modo dele; se ele continua no canal (a busca falhou, ou o modo voltou), a
// conversa abre de novo, com o que o servidor tem. A tela que já saiu do canal não busca: a recusa é
// a da consulta das respostas, que ainda rodava, e o agente já chegou.
watch(
  () => conversas.canal.saida,
  async motivo => {
    if (!motivo) return;
    conversas.canal.saida = null;
    if (!noCanal.value) return;
    await buscarOAgente();
    if (noCanal.value) abrirCanal();
  }
);

const config = computed(() => props.agent.config || {});
// O que a versão publicada decide na conversa do canal, como a página do link o recebe.
const publicada = computed(() => estadoDoCanal.value?.agente || {});
// Com a transcrição desligada, o agente em produção não ouve nota de voz, e o teste que ouvisse
// mentiria. A conversa ainda confere se o navegador grava. Desligada é a regra de
// `helpers/transcricao.js`, a mesma do servidor: só o `false` explícito.
const ouveAudio = computed(() =>
  noCanal.value
    ? Boolean(publicada.value.ouve_audio)
    : !transcricaoRecusada.value && transcreveAudio(config.value)
);
// O rascunho espera texto junto do arquivo (`attachment_needs_text`, aba Voz e anexos): em
// produção ele não responde a mensagem que é só arquivo. O servidor confere de novo, no rascunho.
const anexoPedeTexto = computed(() =>
  Boolean(
    noCanal.value
      ? publicada.value.anexo_pede_texto
      : config.value.attachment_needs_text
  )
);

// A leitura da foto e do PDF é o switch do rascunho (`read_attachments`, que o runtime lê com
// "ausente é desligado"); o PDF depende também do par de modelos, e o servidor manda a resposta pronta
// (`le_documentos`), sem o modelo. "Voz e anexos" é seção de plataforma: o caminho para ligar só
// aparece para quem a vê, e o mesmo vale para o motivo da nota de voz que se conserta lá.
const leAnexos = computed(() =>
  Boolean(
    noCanal.value ? publicada.value.le_anexos : config.value.read_attachments
  )
);
const leDocumentos = computed(() =>
  Boolean(
    noCanal.value ? publicada.value.le_documentos : props.agent.le_documentos
  )
);
const podeLigarAnexos = computed(() => Boolean(ctx.temSecao?.('audio')));
const ligarAnexos = () =>
  ctx.irPara({ section: 'audio', anchor: 'audio-anexos' });

// Alterou dados: uma ferramenta rodou de verdade e mudou algo no sistema do cliente. O rastro fica
// fechado, e a marca sobe para a linha dele, para ninguém sair do teste sem ver o que aconteceu.
const alterouDados = rastro =>
  (rastro.tool_calls || []).some(
    call =>
      call.execution === 'ran' &&
      call.efeito === 'escrita' &&
      call.status === 'ok'
  );
</script>

<template>
  <SectionShell
    data-busca="playground"
    :title="t('POWER_AGENTS.PLAYGROUND.TITLE')"
    :description="
      noCanal
        ? t('POWER_AGENTS.PLAYGROUND.CANAL.DESCRIPTION')
        : t('POWER_AGENTS.PLAYGROUND.DESCRIPTION')
    "
    :saving="null"
  >
    <div
      class="flex items-start gap-3 px-4 py-3 text-sm border rounded-xl border-n-weak bg-n-alpha-1 text-n-slate-11"
      :data-modo="noCanal ? 'canal' : 'motor'"
    >
      <i
        class="mt-0.5 text-base text-n-slate-10"
        :class="noCanal ? 'i-lucide-inbox' : 'i-lucide-flask-conical'"
      />
      <span v-if="noCanal">
        {{
          t('POWER_AGENTS.PLAYGROUND.CANAL.HINT', {
            caixa: estadoDoCanal?.caixa || '…',
          })
        }}
      </span>
      <span v-else>{{ t('POWER_AGENTS.PLAYGROUND.DRAFT_HINT') }}</span>
    </div>
    <!-- No canal quem responde é a versão publicada: sem ela, a mensagem fica sem resposta, e a tela
         diz antes. -->
    <p
      v-if="noCanal && estadoDoCanal?.published === false"
      class="px-4 py-2 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
      role="status"
      data-canal-sem-publicar
    >
      {{ t('POWER_AGENTS.PLAYGROUND.CANAL.NOT_PUBLISHED') }}
    </p>

    <div class="flex flex-col overflow-hidden border rounded-xl border-n-weak">
      <CabecalhoDaConversa
        :nome="agent.bot_name || ''"
        :foto="agent.avatar_url || ''"
        :selo="
          noCanal
            ? t('POWER_AGENTS.PLAYGROUND.CANAL.BADGE')
            : t('POWER_AGENTS.PLAYGROUND.BADGE')
        "
        :pode-recomecar="conversa.turnos.length > 0"
        :recomecando="conversa.recomecando"
        :ocupada="conversa.ocupada"
        :falha-ao-recomecar="conversa.falhaAoRecomecar"
        @recomecar="conversa.recomecar()"
      />
      <div
        v-if="noCanal && canalNaoAbriu"
        class="flex flex-col items-center justify-center gap-3 px-4 text-sm text-center h-[26rem] text-n-slate-11"
        role="alert"
        data-canal-nao-abriu
      >
        <i class="text-2xl i-lucide-cloud-off text-n-slate-10" />
        {{ t('POWER_AGENTS.PLAYGROUND.CANAL.LOAD_ERROR') }}
        <button
          type="button"
          class="font-medium underline text-n-slate-12"
          @click="abrirCanal"
        >
          {{ t('POWER_AGENTS.PLAYGROUND.CANAL.RETRY') }}
        </button>
      </div>
      <div
        v-else-if="noCanal && !estadoDoCanal"
        class="flex items-center justify-center h-[26rem] text-n-slate-10"
        role="status"
        data-canal-abrindo
      >
        <Spinner :size="24" />
      </div>
      <ConversaDeTeste
        v-else
        publico="equipe"
        altura="secao"
        :turnos="conversa.turnos"
        :ocupada="conversa.ocupada"
        :ouve-audio="ouveAudio"
        :anexo-pede-texto="anexoPedeTexto"
        :le-anexos="leAnexos"
        :le-documentos="leDocumentos"
        :ve-voz-e-anexos="podeLigarAnexos"
        @enviar="(texto, arquivos) => conversa.enviar(texto, arquivos)"
        @audio="
          (gravacao, arquivos) => conversa.enviarAudio(gravacao, arquivos)
        "
        @reenviar="turno => conversa.reenviar(turno)"
      >
        <template #nao-analisa>
          {{ t('CONVERSA_DE_TESTE.FILES.NOT_ANALYZED') }}
          <button
            v-if="podeLigarAnexos"
            type="button"
            class="font-medium underline hover:text-n-amber-12"
            data-ligar-anexos
            @click="ligarAnexos"
          >
            {{
              t('POWER_AGENTS.PLAYGROUND.TURN_ON_IN', {
                secao: t('POWER_AGENTS.SECTIONS.AUDIO'),
              })
            }}
          </button>
          <!-- Trocar de seção desmonta o Playground, e a conversa do motor vai junto: quem clica fica
               sabendo antes. A do canal fica na caixa, e volta. -->
          <span
            v-if="podeLigarAnexos && !noCanal && conversa.turnos.length"
            class="ml-1"
            data-apaga-conversa
          >
            {{ t('POWER_AGENTS.PLAYGROUND.TURN_ON_CLEARS') }}
          </span>
        </template>
        <!-- O rastro de cada resposta, fechado: a conversa fica igual à da página, e o rastro é o que
             o Playground tem a mais. -->
        <template #depois-da-resposta="{ turno }">
          <details v-if="turno.rastro" class="group" data-rastro>
            <summary
              class="flex items-center gap-1.5 text-xs list-none cursor-pointer select-none w-fit text-n-slate-10 hover:text-n-slate-12 [&::-webkit-details-marker]:hidden"
            >
              <i
                class="transition-transform size-3.5 i-lucide-chevron-right group-open:rotate-90"
              />
              {{ t('POWER_AGENTS.PLAYGROUND.TRACE') }}
              <span
                v-if="alterouDados(turno.rastro)"
                class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-blue-3 text-n-blue-11"
                data-alterou-dados
              >
                {{ t('POWER_AGENTS.PLAYGROUND.WROTE') }}
              </span>
            </summary>
            <RastroDoTurno
              class="mt-2"
              :turno="turno.rastro"
              :nome-do-time="nomeDoTime"
            />
          </details>
        </template>
      </ConversaDeTeste>
    </div>

    <p class="text-xs text-n-slate-10">
      {{
        noCanal
          ? t('POWER_AGENTS.PLAYGROUND.CANAL.FOOTER')
          : t('POWER_AGENTS.PLAYGROUND.HINT')
      }}
    </p>
  </SectionShell>
</template>
